import {Injectable} from '@angular/core';
import { HttpClient } from '@angular/common/http';
import {Observable} from 'rxjs';

import {ApiLocationService} from '@services/api-location/api-location.service';

import {UserDefinedTabSettingsRequest} from '@models/sidebar/request/user-defined-tab-settings-request';
import {UserMenuItem} from '@models/sidebar/response/user-menu-item';
import {BaseMenuItem} from '@models/sidebar/response/base-menu-item';

import {HTTPS_REQUEST_OPTIONS} from '@constants/https-request-options/https-request-options';
import {PageItem} from '@models/sidebar/page-item';
import {MenuNotification, OperationType} from '@models/sidebar/menu-notification';
import {LoadingIndicatorService} from '@services/loading-indicator/loading-indicator.service';
import {NotificationService} from '@services/notification/notification.service';
import {MenuNotificationService} from '@services/menu-notification/menu-notification.service';
import {TranslateService} from '@ngx-translate/core';
import {MenuItemType} from '@models/sidebar/menu-item-type';
import {MenuItem} from '@models/sidebar/menu-item';
import {
  FavoriteNotificationService
} from '@services/left-menu/favorites/favorite-notification/favorite-notification.service';
import {GlobalHelperService} from '@services/global-helper/global-helper.service';

@Injectable({
  providedIn: 'root'
})
export class UserDefinedPageService {

  private CREATE_USER_DEFINED_PAGE_URL_SUFFIX = '/api/web/base/:machineId/userMenu/userDefinedPage/:parentId/:isParentMachine';
  private DELETE_USER_DEFINED_PAGE_URL_SUFFIX = '/api/web/base/:machineId/userMenu/userDefinedPage/:tabId/itemId/:itemId/parent/:parentId/isMachine/:isParentMachine/shared/:isShared';
  private RENAME_USER_DEFINED_PAGE_URL_SUFFIX = '/api/web/base/:machineId/userMenu/userDefinedPage/rename/itemId/:itemId/:parentId/:isParentMachine/:isShared';
  private SHARE_USER_DEFINED_PAGE_URL_SUFFIX = '/api/web/base/:machineId/userMenu/userDefinedPage/share/:tabId/:parentId/:isParentMachine/:parentName';
  private UNSHARE_USER_DEFINED_PAGE_URL_SUFFIX = '/api/web/base/:machineId/userMenu/userDefinedPage/unshare/:tabId';

  private readonly savingIndicatorMessage = '';
  private readonly errorCreatingPage = '';
  private readonly successCreatingPage = '';
  private readonly errorRenamingPage = '';
  private readonly successRenamingPage = '';

  private readonly BASE_URL: string;

  constructor(private http: HttpClient,
              private apiLocation: ApiLocationService,
              private loadingIndicator: LoadingIndicatorService,
              private notificationService: NotificationService,
              private menuNotificationService: MenuNotificationService,
              private translateService: TranslateService,
              private favoriteNotificationService: FavoriteNotificationService,
              private globalHelper: GlobalHelperService,) {
    this.BASE_URL = this.apiLocation.getLocation();
    this.savingIndicatorMessage = this.translateService.instant('Saving');
    this.errorCreatingPage = this.translateService.instant('Error creating user defined page.');
    this.successCreatingPage = this.translateService.instant('User defined page was created.');
    this.errorRenamingPage = this.translateService.instant('Error renaming user defined page.');
    this.successRenamingPage = this.translateService.instant('User defined page was renamed.');
  }

  static pageIsInsideNode(pageItem: PageItem): boolean {
    return pageItem.parentId !== pageItem.machineId;
  }

  static pageIsOutsideNode(pageItem: PageItem): boolean {
    return pageItem.parentId === pageItem.machineId;
  }

  static setNewUserDefinedPageProperties(pageItem: PageItem, userMenuItem: UserMenuItem, machineId: string, firebaseUserId: string): void {
    pageItem.machineId = machineId;
    pageItem.firebaseUserId = firebaseUserId;
    pageItem.pageId = userMenuItem.pageId;
    pageItem.id = userMenuItem.id;
    pageItem.parent = userMenuItem.parent;
    pageItem.index = userMenuItem.index;
    pageItem.canBeAltered = true;
    pageItem.parentId = machineId;
  }

  static setNewName(pageItem: PageItem, baseMenuItems: PageItem[]): void {
    pageItem.name = UserDefinedPageService.getUpdatedPageName(pageItem.name, baseMenuItems);
  }

  public create(userMenuItems: PageItem[], pageItem: PageItem, machineId: string | null, currentPageName: string, parentId: string | null, isAdminOrHigher: boolean, isParentMachine: boolean, firebaseUserId: string): void {
    if (machineId && parentId) {
      this.loadingIndicator.show(this.savingIndicatorMessage);
      const request = new UserDefinedTabSettingsRequest();
      request.tabDetails.tabName = pageItem.name;
      request.tabDetails.tabId = -1;
      this.createUserDefinedPage(machineId, parentId, isParentMachine, request).subscribe((newUserMenuItem: UserMenuItem) => {
        this.menuNotificationService.broadcast(new MenuNotification(OperationType.ADD, parentId, pageItem.name, ''));
        UserDefinedPageService.setNewUserDefinedPageProperties(pageItem, newUserMenuItem, machineId, firebaseUserId);
        userMenuItems.push(pageItem);
        this.loadingIndicator.hide();
        this.notificationService.success(this.successCreatingPage, currentPageName, false);
      }, (error) => {
        this.notificationService.error(this.errorCreatingPage, error, currentPageName, true);
        this.loadingIndicator.hide();
      });
    }
  }

  createUserDefinedPage(machineId: string, parentId: string, isParentMachine: boolean, request: UserDefinedTabSettingsRequest): Observable<any> {
    let url: string = this.BASE_URL + this.CREATE_USER_DEFINED_PAGE_URL_SUFFIX;
    url = url.replace(':machineId', machineId)
    .replace(':parentId', parentId)
    .replace(':isParentMachine', String(isParentMachine));
    return this.http.post(url, request, HTTPS_REQUEST_OPTIONS);
  }

  public delete(machineId: string, tabId: string, itemId: string, parentId: string, isParentMachine: boolean, isSharedPage: boolean): Observable<any> {
    let url: string = this.BASE_URL + this.DELETE_USER_DEFINED_PAGE_URL_SUFFIX;
    url = url.replace(':machineId', String(machineId))
    .replace(':tabId', tabId)
    .replace(':itemId', itemId)
    .replace(':parentId', parentId)
    .replace(':isParentMachine', String(isParentMachine))
    .replace(':isShared', String(isSharedPage));
    return this.http.delete(url, HTTPS_REQUEST_OPTIONS);
  }

  public rename(pageItem: PageItem, machineId: string | null, parentId: string | null, currentPageName: string, isParentMachine: boolean, nameFallback?: Object): void {
    if (UserDefinedPageService.pageIsInsideNode(pageItem)) {
      // this happens because the menu structure is not updated as soon as a page is moved around
      return this.onEditPageItemInsideNode(pageItem, machineId, parentId, currentPageName, isParentMachine, <Object>nameFallback);
    }

    if (machineId && pageItem.id && pageItem.pageId && parentId) {
      this.loadingIndicator.show(this.savingIndicatorMessage);
      const userDefinedTab = new UserDefinedTabSettingsRequest();
      userDefinedTab.tabDetails.userTabId = pageItem.pageId;
      userDefinedTab.tabDetails.tabName = pageItem.name;
      const isShared = pageItem.type === MenuItemType.USER_SHARED_PAGE;
      const itemId: string = pageItem.id;
      this.renamePageName(machineId, itemId, parentId, isParentMachine, isShared, userDefinedTab).subscribe((result: UserMenuItem) => {
        this.globalHelper.handleUpdatedPage(result.name, parentId, currentPageName, itemId, this.successRenamingPage);
      }, (error) => {
        this.notificationService.error(this.errorRenamingPage, error, currentPageName, true);
        this.loadingIndicator.hide();
      });
    }
  }

  onEditPageItemInsideNode(pageItem: PageItem, machineId: string | null, parentId: string | null, currentPageName: string, isParentMachine: boolean, nameFallback: Object): void {
    if (UserDefinedPageService.pageIsOutsideNode(pageItem)) {
      // this happens because the menu structure is not updated as soon as a page is moved around
      return this.rename(pageItem, machineId, parentId, currentPageName, isParentMachine, nameFallback);
    }

    if (machineId && pageItem.id && pageItem.pageId && pageItem.parentId) {
      this.loadingIndicator.show(this.savingIndicatorMessage);
      const userDefinedTab = new UserDefinedTabSettingsRequest();
      userDefinedTab.tabDetails.userTabId = pageItem.pageId;
      userDefinedTab.tabDetails.tabName = pageItem.name;
      const itemId: string = pageItem.id;
      this.renamePageName(machineId, itemId, pageItem.parentId, false, false, userDefinedTab).subscribe((result) => {
        this.menuNotificationService.broadcast(new MenuNotification(OperationType.UPDATE_INSIDE_PARENT, parentId, '', result.label));
        this.favoriteNotificationService.broadcast(new MenuNotification(OperationType.UPDATE_NAME, itemId, '', result.tabDetails.tabName));
        this.loadingIndicator.hide();
        this.notificationService.success(this.successRenamingPage, currentPageName, false);
      }, (error) => {
        this.notificationService.error(this.errorRenamingPage, error, currentPageName, true);
        this.loadingIndicator.hide();
      });
    }
  }

  renamePageName(machineId: string, itemId: string, parentId: string, isParentMachine: boolean, isShared: boolean, request: UserDefinedTabSettingsRequest): Observable<any> {
    let url: string = this.BASE_URL + this.RENAME_USER_DEFINED_PAGE_URL_SUFFIX;
    url = url.replace(':machineId', String(machineId))
    .replace(':itemId', itemId)
    .replace(':parentId', parentId)
    .replace(':isParentMachine', String(isParentMachine))
    .replace(':isShared', String(isShared));
    return this.http.post(url, request, HTTPS_REQUEST_OPTIONS);
  }

  public share(machineId: string, tabId: string, parentId: string, isParentMachine: boolean, request: UserMenuItem, parentName: string): Observable<any> {
    let url: string = this.BASE_URL + this.SHARE_USER_DEFINED_PAGE_URL_SUFFIX;
    url = url.replace(':machineId', machineId)
    .replace(':tabId', tabId)
    .replace(':parentId', parentId)
    .replace(':isParentMachine', String(isParentMachine))
    .replace(':parentName', parentName);
    return this.http.post(url, request, HTTPS_REQUEST_OPTIONS);
  }

  public unshare(machineId: string, tabId: string, request: BaseMenuItem): Observable<any> {
    let url: string = this.BASE_URL + this.UNSHARE_USER_DEFINED_PAGE_URL_SUFFIX;
    url = url.replace(':machineId', machineId)
    .replace(':tabId', tabId);
    return this.http.post(url, request, HTTPS_REQUEST_OPTIONS);
  }

  public getRenameObservable(pageItem: PageItem, machineId: string | null, parentId: string | null | undefined, currentPageName: string, isParentMachine: boolean): Observable<any> | null {
    if (pageItem.pageId) {
      const userDefinedTab = new UserDefinedTabSettingsRequest();
      userDefinedTab.tabDetails.userTabId = pageItem.pageId;
      userDefinedTab.tabDetails.tabName = pageItem.name;
      const isShared = pageItem.type === MenuItemType.USER_SHARED_PAGE;

      if (machineId && pageItem.id && parentId) {
        return this.renamePageName(machineId, pageItem.id, parentId, isParentMachine, isShared, userDefinedTab);
      }
    }
    return null;
  }

  public getShareObservable(pageItem: PageItem, menuItem: MenuItem, parentId: string | null | undefined, isParentMachine: boolean, request: UserMenuItem): Observable<any> | null {
    const parentName: string = UserDefinedPageService.getParentName(pageItem, isParentMachine, menuItem);
    if (menuItem.machineId && pageItem.pageId && parentId) {
      return this.share(menuItem.machineId, pageItem.pageId, parentId, isParentMachine, request, parentName);
    }
    return null;
  }

  private static getParentName(pageItem: PageItem, isParentMachine: boolean, menuItem: MenuItem): string {
    if (isParentMachine && pageItem.menuItemParent) {
      return pageItem.menuItemParent?.name;
    }
    return menuItem.name;
  }

  private static getUpdatedPageName(name: string, baseMenuItems: PageItem[]): string {
    if (baseMenuItems.filter(m => m.name === name).length === 0) {
      return name;
    }
    if (name.includes('_')) {
      name = this.incrementNumberInPageName(name);
    } else {
      name = name + '_1';
    }
    return UserDefinedPageService.getUpdatedPageName(name, baseMenuItems);
  }

  private static incrementNumberInPageName(name: string): string {
    const parts = name.split('_');
    const possibleNumber = parts[parts.length - 1];
    const number = Number(possibleNumber);
    if (isNaN(number)) {
      return name + '_1';
    } else {
      const newName = '_' + (number + 1);
      return name.replace('_' + possibleNumber, newName);
    }
  }

}
